/* eslint-disable no-underscore-dangle */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Meteor } from 'meteor/meteor';
import { get, isEqual, isEmpty } from 'lodash';
import turf from '@turf/turf';

import * as videoOps from '../../../api/Videos/video-operations';

import { featurePoint2LatLng, featureGetZoom } from '../../../modules/geojson-utilities';

import LeafletMap from '../../components/LeafletMap';

let addOnlineBaseMap;
let drawCustomButtons;
let drawRPAPosition;
let drawVideoControlPoints;
let drawVideoTrack;
let drawMapLayers;

class MapContainerVideoSynchronizer extends Component {
  state = {
    clientModulesReady: false,
    isLeafletMapLoaded: get(this, 'videoMapComponent.current.state.isMapLoaded', false),
    isVideoMapLoaded: false
  };

  controlPointsLayer = null;

  rpaPositionLayer = null;

  trackLayer = null;

  videoMapComponent = React.createRef();

  videoMap = null;

  componentDidMount() {
    const { clientModulesReady } = this.state;
    if (!clientModulesReady) {
      this.loadClientModules();
    }
  }

  componentDidUpdate(prevProps) {
    const {
      updatedVideo,
      currentRPAPosition,
      rpaBearing,
      track,
      updateMapLocation,
      changeVideoContainerStateProperty
    } = this.props;
    const { clientModulesReady, isLeafletMapLoaded, isVideoMapLoaded } = this.state;
    const {
      updatedVideo: prevUpdatedVideo,
      currentRPAPosition: prevCurrentRPAPosition,
      rpaBearing: prevRPABearing
    } = prevProps;

    if (clientModulesReady && isLeafletMapLoaded && !isVideoMapLoaded) {
      this.initializeMap();
    }

    if (isVideoMapLoaded && updateMapLocation) {
      this.videoMap.setView(featurePoint2LatLng(currentRPAPosition));
      if (updateMapLocation) changeVideoContainerStateProperty('updateMapLocation', false);
    }

    if (
      isVideoMapLoaded &&
      !isEqual(videoOps.getTrack(updatedVideo), videoOps.getTrack(prevUpdatedVideo))
    ) {
      drawVideoTrack({
        leafletMap: this.videoMap,
        track,
        trackLayer: this.trackLayer,
        isUpdate: true,
        isSynchronizer: true
      });
    }

    if (
      isVideoMapLoaded &&
      (!isEqual(currentRPAPosition, prevCurrentRPAPosition) || !isEqual(rpaBearing, prevRPABearing))
    ) {
      drawRPAPosition({
        leafletMap: this.videoMap,
        currentRPAPosition,
        rpaBearing,
        RPAPositionLayer: this.rpaPositionLayer,
        isUpdate: true
      });
    }

    if (
      isVideoMapLoaded &&
      !isEqual(videoOps.getControlPoints(updatedVideo), videoOps.getControlPoints(prevUpdatedVideo))
    ) {
      drawVideoControlPoints({
        leafletMap: this.videoMap,
        controlPoints: videoOps.getControlPoints(updatedVideo),
        onClickControlPoint: ({ controlPoint, controlPointTime }) => {
          changeVideoContainerStateProperty('updateVideo', true);
          changeVideoContainerStateProperty('videoTime', controlPointTime);
          changeVideoContainerStateProperty('selectedControlPoint', controlPoint);
        },
        controlPointsLayer: this.controlPointsLayer,
        isUpdate: true
      });
    }
  }

  updateMapState = newState => this.setState({ isLeafletMapLoaded: newState });

  initializeMap = async () => {
    const {
      updatedVideo,
      currentRPAPosition,
      rpaBearing,
      track,
      changeVideoContainerStateProperty,
      initializeLoop,
      onClickOnMap
    } = this.props;

    const location = turf.point(videoOps.getLatLng0(updatedVideo), { zoom: 16 });

    this.videoMap = get(this, 'videoMapComponent.current.leafletMap');
    this.videoMap.setView(featurePoint2LatLng(location), featureGetZoom(location));
    this.videoMap.on('click', event => onClickOnMap(event.latlng));

    const { baseMaps, layersMaps } = videoOps.getGeomotionMap(updatedVideo);
    const base = !isEmpty(baseMaps) ? baseMaps : [];
    const layer = !isEmpty(layersMaps) ? layersMaps : [];

    if (!isEmpty(baseMaps)) {
      await drawMapLayers({
        layers: layer,
        baseMaps: base,
        popupProp: false,
        leafletMap: this.videoMap,
        layersToDisplay: layer,
        baseMapsToDisplay: base,
        isUpdate: false,
        baseMapsTransparency: [],
        layersTransparency: [],
        baseMapsZoom: [],
        layersZoom: []
      });
    } else addOnlineBaseMap(this.videoMap);

    this.trackLayer = drawVideoTrack({
      leafletMap: this.videoMap,
      track,
      isSynchronizer: true
    });

    this.rpaPositionLayer = drawRPAPosition({
      leafletMap: this.videoMap,
      currentRPAPosition,
      rpaBearing
    });

    this.controlPointsLayer = drawVideoControlPoints({
      leafletMap: this.videoMap,
      controlPoints: videoOps.getControlPoints(updatedVideo),
      onClickControlPoint: controlPoint => {
        changeVideoContainerStateProperty('updateVideo', true);
        changeVideoContainerStateProperty('videoTime', controlPoint.controlPointTime);
        changeVideoContainerStateProperty('selectedControlPoint', controlPoint.controlPoint);
      }
    });

    drawCustomButtons({
      map: {
        selectedButtons: {
          Attribution: true,
          Home: true,
          QRCode: false,
          Scale: true,
          Search: false,
          Title: false,
          Zoom: true
        },
        mapLocation: location,
        name: videoOps.getName(updatedVideo)
      },
      type: 'video',
      leafletMap: this.videoMap
    });

    this.setState({ isVideoMapLoaded: true });

    initializeLoop();
  };

  async loadClientModules() {
    if (Meteor.isClient) {
      ({
        addOnlineBaseMap,
        drawCustomButtons,
        drawRPAPosition,
        drawVideoControlPoints,
        drawVideoTrack,
        drawMapLayers
      } = await import('../../../modules/leaflet-operations'));
      this.setState({ clientModulesReady: true });
    }
  }

  render() {
    const { deployEnv } = this.props;

    return (
      <div>
        <LeafletMap
          mapId="videoMap"
          headerType="8"
          ref={this.videoMapComponent}
          updateMapState={this.updateMapState}
          deployEnv={deployEnv}
        />
      </div>
    );
  }
}

MapContainerVideoSynchronizer.propTypes = {
  currentRPAPosition: PropTypes.object.isRequired,
  deployEnv: PropTypes.string.isRequired,
  rpaBearing: PropTypes.number.isRequired,
  track: PropTypes.object.isRequired,
  updatedVideo: PropTypes.object.isRequired,
  updateMapLocation: PropTypes.bool.isRequired,
  changeVideoContainerStateProperty: PropTypes.func.isRequired,
  initializeLoop: PropTypes.func.isRequired,
  onClickOnMap: PropTypes.func.isRequired
};

export default MapContainerVideoSynchronizer;
